import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LocationAutocomplete, type PlaceData } from "@/components/ui/location-autocomplete";
import { getTruckRoute, getGeocodingSuggestions } from "@/lib/mapbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import {
  Beaker, Sparkles, ThumbsUp, ThumbsDown, Star, Loader2,
  AlertTriangle, CheckCircle, Clock, ChevronDown, Bell, Vote,
  TrendingUp, Gauge, RouteIcon, ShieldAlert, CalendarIcon,
  CloudRain, Car, Construction, Timer
} from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { DemoHint } from "@/components/demo/DemoHint";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const CURRENT_FEATURE_KEY = "lane_predictor";

interface FeatureStatRow {
  feature_key: string;
  status: "live" | "beta" | "coming_soon" | "archived";
  display_name: string;
  helpful_count: number;
  not_helpful_count: number;
  make_core_count: number;
  prioritize_count: number;
  notify_me_count: number;
  active_testers_7d: number;
  user_voted_helpful: boolean | null;
  user_voted_not_helpful: boolean | null;
  user_voted_make_core: boolean | null;
  user_voted_prioritize: boolean | null;
  user_notified: boolean;
}

const predictionLabels: Record<string, string> = {
  on_time: "On Time", tight: "Tight", late_risk: "Late Risk", late_likely: "Late Likely",
  "On Time": "On Time", "Tight": "Tight", "Late Risk": "Late Risk",
};
const riskLabels: Record<string, string> = {
  low: "Low", medium: "Medium", high: "High",
  Low: "Low", Medium: "Medium", High: "High",
};
const fmtPrediction = (v: string) => predictionLabels[v] || v;
const fmtRisk = (v: string) => riskLabels[v] || v;

interface RouteImpact {
  severity: "none" | "low" | "moderate" | "high";
  summary: string;
  delayMinutes: number;
}

interface PredictionResult {
  prediction: string;
  confidence: number;
  riskLevel: string;
  detectedIssues: string[];
  recommendation: string;
  remainingMiles: number;
  requiredSpeed: number;
  reasoning: string[];
  weatherImpact?: RouteImpact;
  trafficImpact?: RouteImpact;
  constructionImpact?: RouteImpact;
  estimatedArrival?: string;
  bufferHours?: number;
}

const TRACKING_PLACEHOLDER = `Location Update  Mccordsville, IN  7:39 AM
Location Update  Mccordsville, IN  6:38 AM
Location Update  Mccordsville, IN  5:38 AM`;

// Freight-time parsing, location extraction, route-risk heuristic, and
// datetime combiners now live in the shared LanePredictor lib so the
// shipper widget calls the same helpers.
import {
  parseFreightTime,
  extractLastLocation,
  combineDateAndTime,
  formatLocalDateTime,
  generateRouteRiskEstimate,
} from "@/lib/lanePredictor";

type DriverHoursStatus = "fresh" | "partial" | "unknown";
type MovementStatus = "moving" | "parked" | "unknown";

export default function AtlasFreightLabPage() {
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [originPlaceData, setOriginPlaceData] = useState<PlaceData | null>(null);
  const [destinationPlaceData, setDestinationPlaceData] = useState<PlaceData | null>(null);

  // Appointment state: date + time text
  const [pickupDate, setPickupDate] = useState<Date | undefined>(undefined);
  const [pickupTimeText, setPickupTimeText] = useState("");
  const [deliveryDate, setDeliveryDate] = useState<Date | undefined>(undefined);
  const [deliveryTimeText, setDeliveryTimeText] = useState("");
  const [pickupTimeError, setPickupTimeError] = useState("");
  const [trackingError, setTrackingError] = useState("");
  const [deliveryTimeError, setDeliveryTimeError] = useState("");
  const [analysisTimeText, setAnalysisTimeText] = useState("");
  const [analysisTimeError, setAnalysisTimeError] = useState("");

  const [truckType, setTruckType] = useState<"solo" | "team">("solo");
  const [miles, setMiles] = useState("");
  const [remainingMiles, setRemainingMiles] = useState("");
  const [remainingMilesAuto, setRemainingMilesAuto] = useState(false);
  const [remainingMilesLoading, setRemainingMilesLoading] = useState(false);
  const [milesLoading, setMilesLoading] = useState(false);
  const remainingMilesManualRef = useRef(false);
  const [trackingLogs, setTrackingLogs] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PredictionResult | null>(null);
  const [reasoningOpen, setReasoningOpen] = useState(false);

  // Current location fallback (when no tracking logs)
  const [currentLocationOverride, setCurrentLocationOverride] = useState("");
  const [currentLocationPlaceData, setCurrentLocationPlaceData] = useState<PlaceData | null>(null);

  // Auto-populate analysis time with current local time
  useEffect(() => {
    const now = new Date();
    const h = now.getHours();
    const m = now.getMinutes();
    const ampm = h >= 12 ? "pm" : "am";
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    setAnalysisTimeText(`${h12}:${String(m).padStart(2, "0")}${ampm}`);
  }, []);

  // HOS & Movement
  const [driverHoursStatus, setDriverHoursStatus] = useState<DriverHoursStatus>("unknown");
  const [drivingHoursRemaining, setDrivingHoursRemaining] = useState("");
  const [currentMovementStatus, setCurrentMovementStatus] = useState<MovementStatus>("unknown");

  const handleOriginChange = (value: string, placeData?: PlaceData) => {
    setOrigin(value);
    if (placeData) setOriginPlaceData(placeData);
  };

  const handleDestinationChange = (value: string, placeData?: PlaceData) => {
    setDestination(value);
    if (placeData) setDestinationPlaceData(placeData);
  };

  // Auto-calculate miles when both locations are selected
  useEffect(() => {
    if (!originPlaceData || !destinationPlaceData) return;
    if (!originPlaceData.latitude || !destinationPlaceData.latitude) return;
    let cancelled = false;
    const calc = async () => {
      setMilesLoading(true);
      try {
        const route = await getTruckRoute(
          [originPlaceData.longitude, originPlaceData.latitude],
          [destinationPlaceData.longitude, destinationPlaceData.latitude]
        );
        if (!cancelled && route) {
          setMiles(String(route.distance));
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setMilesLoading(false); }
    };
    calc();
    return () => { cancelled = true; };
  }, [originPlaceData, destinationPlaceData]);

  // Auto-calculate remaining miles from tracking logs + destination
  const autoCalcTimerRef = useRef<number | undefined>(undefined);

  const autoCalcRemainingMiles = useCallback(async (logs: string, destPlace: PlaceData | null) => {
    if (remainingMilesManualRef.current) return; // user manually edited, don't overwrite
    if (!destPlace?.latitude || !destPlace?.longitude) return;

    const lastLoc = extractLastLocation(logs);
    if (!lastLoc) {
      setRemainingMilesAuto(false);
      return;
    }

    setRemainingMilesLoading(true);
    try {
      const geoData = await getGeocodingSuggestions(lastLoc);
      const feature = geoData?.features?.[0];
      const coords = feature?.center || feature?.geometry?.coordinates;
      if (!coords || coords.length < 2) return;

      const [lon, lat] = coords;
      const route = await getTruckRoute(
        [lon, lat],
        [destPlace.longitude, destPlace.latitude]
      );
      if (route && !remainingMilesManualRef.current) {
        setRemainingMiles(String(route.distance));
        setRemainingMilesAuto(true);
      }
    } catch {
      // silent — leave field as-is
    } finally {
      setRemainingMilesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!trackingLogs.trim() || !destinationPlaceData) return;
    if (remainingMilesManualRef.current) return;

    if (autoCalcTimerRef.current) window.clearTimeout(autoCalcTimerRef.current);
    autoCalcTimerRef.current = window.setTimeout(() => {
      autoCalcRemainingMiles(trackingLogs, destinationPlaceData);
    }, 800);

    return () => {
      if (autoCalcTimerRef.current) window.clearTimeout(autoCalcTimerRef.current);
    };
  }, [trackingLogs, destinationPlaceData, autoCalcRemainingMiles]);

  // Auto-calc remaining miles from current location fallback (no tracking logs)
  useEffect(() => {
    if (trackingLogs.trim()) return; // tracking logs take priority
    if (remainingMilesManualRef.current) return;
    if (!currentLocationPlaceData?.latitude || !currentLocationPlaceData?.longitude) return;
    if (!destinationPlaceData?.latitude || !destinationPlaceData?.longitude) return;

    let cancelled = false;
    const calc = async () => {
      setRemainingMilesLoading(true);
      try {
        const route = await getTruckRoute(
          [currentLocationPlaceData.longitude, currentLocationPlaceData.latitude],
          [destinationPlaceData.longitude, destinationPlaceData.latitude]
        );
        if (!cancelled && route && !remainingMilesManualRef.current) {
          setRemainingMiles(String(route.distance));
          setRemainingMilesAuto(true);
        }
      } catch { /* silent */ }
      finally { if (!cancelled) setRemainingMilesLoading(false); }
    };
    calc();
    return () => { cancelled = true; };
  }, [currentLocationPlaceData, destinationPlaceData, trackingLogs]);

// generateRouteRiskEstimate, combineDateAndTime, and formatLocalDateTime
// are shared with the LanePredictorWidget. See src/lib/lanePredictor.


  const { isDemoUser } = useAuth();
  const queryClient = useQueryClient();
  const { data: featureStats } = useQuery<FeatureStatRow[]>({
    queryKey: ["feature-stats"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_feature_stats");
      if (error) throw error;
      return (data || []) as FeatureStatRow[];
    },
    staleTime: 30_000,
  });

  const currentFeature = featureStats?.find(f => f.feature_key === CURRENT_FEATURE_KEY);
  const loadCheckFeature = featureStats?.find(f => f.feature_key === "load_check");
  const communityVotesTotal = (featureStats || []).reduce(
    (s, f) => s + Number(f.helpful_count) + Number(f.not_helpful_count) + Number(f.make_core_count) + Number(f.prioritize_count),
    0,
  );
  const mostRequested = (featureStats || [])
    .filter(f => f.status === "coming_soon")
    .sort((a, b) => Number(b.prioritize_count) - Number(a.prioritize_count))[0];

  const labStatusLabel: Record<string, string> = {
    live: "Live", beta: "Beta", coming_soon: "Coming Soon", archived: "Archived",
  };

  const blockDemo = () => {
    if (isDemoUser) {
      toast({ title: "Demo accounts can't vote", description: "Sign up to participate in shaping the Lab.", variant: "destructive" });
      return true;
    }
    return false;
  };

  const castVote = async (
    featureKey: string,
    voteType: "helpful" | "not_helpful" | "make_core" | "prioritize",
  ) => {
    if (blockDemo()) return;
    const stats = queryClient.getQueryData<FeatureStatRow[]>(["feature-stats"]) || [];
    const row = stats.find(f => f.feature_key === featureKey);
    const userKey = `user_voted_${voteType}` as keyof FeatureStatRow;
    const countKey = `${voteType}_count` as keyof FeatureStatRow;
    const alreadyVoted = !!row?.[userKey];

    // Optimistic
    queryClient.setQueryData<FeatureStatRow[]>(["feature-stats"], (prev) =>
      (prev || []).map(f => {
        if (f.feature_key !== featureKey) return f;
        const next = { ...f } as any;
        if (alreadyVoted) {
          next[userKey] = false;
          next[countKey] = Math.max(0, Number(f[countKey]) - 1);
        } else {
          next[userKey] = true;
          next[countKey] = Number(f[countKey]) + 1;
          // Helpful/Not Helpful exclusivity
          if (voteType === "helpful" && f.user_voted_not_helpful) {
            next.user_voted_not_helpful = false;
            next.not_helpful_count = Math.max(0, Number(f.not_helpful_count) - 1);
          }
          if (voteType === "not_helpful" && f.user_voted_helpful) {
            next.user_voted_helpful = false;
            next.helpful_count = Math.max(0, Number(f.helpful_count) - 1);
          }
        }
        return next as FeatureStatRow;
      }),
    );

    const { error } = alreadyVoted
      ? await supabase.rpc("remove_feature_vote", { p_feature_key: featureKey, p_vote_type: voteType })
      : await supabase.rpc("cast_feature_vote", { p_feature_key: featureKey, p_vote_type: voteType });

    if (error) {
      toast({ title: "Vote failed", description: error.message, variant: "destructive" });
    }
    queryClient.invalidateQueries({ queryKey: ["feature-stats"] });
  };

  const setNotify = async (featureKey: string) => {
    if (blockDemo()) return;
    queryClient.setQueryData<FeatureStatRow[]>(["feature-stats"], (prev) =>
      (prev || []).map(f =>
        f.feature_key === featureKey
          ? { ...f, user_notified: true, notify_me_count: Number(f.notify_me_count) + (f.user_notified ? 0 : 1) }
          : f,
      ),
    );
    const { error } = await supabase.rpc("set_feature_notify", { p_feature_key: featureKey });
    if (error) {
      toast({ title: "Could not subscribe", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "You'll be notified", description: "We'll let you know when this feature goes live." });
    }
    queryClient.invalidateQueries({ queryKey: ["feature-stats"] });
  };


  const handleAnalyze = async () => {
    // Validate time inputs
    let hasError = false;
    const parsedPickupTime = parseFreightTime(pickupTimeText);
    const parsedDeliveryTime = parseFreightTime(deliveryTimeText);

    if (pickupTimeText && !parsedPickupTime) {
      setPickupTimeError("Invalid time. Try 10am, 14:00, or 1200.");
      hasError = true;
    } else {
      setPickupTimeError("");
    }
    if (deliveryTimeText && !parsedDeliveryTime) {
      setDeliveryTimeError("Invalid time. Try 10am, 14:00, or 1200.");
      hasError = true;
    } else {
      setDeliveryTimeError("");
    }
    if (hasError) return;

    // Validate analysis time
    const parsedAnalysisTime = parseFreightTime(analysisTimeText);
    if (analysisTimeText && !parsedAnalysisTime) {
      setAnalysisTimeError("Invalid time. Try 10am, 14:00, or 1200.");
      hasError = true;
    } else {
      setAnalysisTimeError("");
    }
    if (hasError) return;

    const hasTrackingOrLocation = trackingLogs.trim() || currentLocationPlaceData;
    if (!hasTrackingOrLocation) {
      setTrackingError("Add tracking logs or enter current truck location to calculate Remaining Miles.");
      hasError = true;
    } else {
      setTrackingError("");
    }
    if (!origin || !destination || !pickupDate || !parsedPickupTime || !deliveryDate || !parsedDeliveryTime) {
      toast({ title: "Missing fields", description: "Please fill in origin, destination, and pickup/delivery dates and times.", variant: "destructive" });
      return;
    }
    if (hasError) return;

    const pickupISO = combineDateAndTime(pickupDate, parsedPickupTime);
    const deliveryISO = combineDateAndTime(deliveryDate, parsedDeliveryTime);

    // ── Compute hoursUntilDelivery locally (pure local Date math, no UTC conversion) ──
    const [delH, delM] = parsedDeliveryTime.split(":").map(Number);
    const deliveryLocal = new Date(deliveryDate);
    deliveryLocal.setHours(delH, delM, 0, 0);

    const analysisLocal = new Date();
    if (parsedAnalysisTime) {
      const [nowH, nowM] = parsedAnalysisTime.split(":").map(Number);
      analysisLocal.setHours(nowH, nowM, 0, 0);
    }

    const hoursUntilDelivery = Math.max(0, (deliveryLocal.getTime() - analysisLocal.getTime()) / 3_600_000);

    // Remaining Miles must come from the auto-calculator (tracking-derived OR current location → destination).
    // Never fall back to total trip miles — that produces dangerously wrong required-speed numbers.
    if (remainingMilesLoading) {
      setTrackingError("Remaining miles are still calculating. Please wait a moment and retry.");
      return;
    }
    const rmMiles = remainingMiles ? Number(remainingMiles) : NaN;
    if (!Number.isFinite(rmMiles) || rmMiles <= 0) {
      setTrackingError("Remaining miles not yet calculated. Wait for the badge to render, then retry.");
      return;
    }
    const effectiveRemainingMiles = rmMiles;
    const precomputedRequiredSpeed = hoursUntilDelivery > 0
      ? Math.round((effectiveRemainingMiles / hoursUntilDelivery) * 10) / 10
      : undefined;

    setLoading(true);
    setResult(null);
    try {
      const routeRisk = generateRouteRiskEstimate(origin, destination);
      const analysisTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const analysisTimeLocalDisplay = analysisLocal.toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
      });

      const { data, error } = await supabase.functions.invoke("atlas-lane-predictor", {
        body: {
          origin, destination,
          pickupTime: pickupISO,
          deliveryTime: deliveryISO,
          pickupTimeLocal: formatLocalDateTime(pickupDate, parsedPickupTime),
          deliveryTimeLocal: formatLocalDateTime(deliveryDate, parsedDeliveryTime),
          truckType,
          totalTripMiles: miles ? Number(miles) : undefined,
          remainingMiles: effectiveRemainingMiles,
          trackingLogs,
          originLat: originPlaceData?.latitude, originLon: originPlaceData?.longitude,
          destLat: destinationPlaceData?.latitude, destLon: destinationPlaceData?.longitude,
          analysisTime: analysisLocal.toISOString(),
          analysisTimeLocal: analysisTimeLocalDisplay,
          analysisTimezone,
          hoursUntilDelivery,
          precomputedRequiredSpeed,
          driverHoursStatus,
          drivingHoursRemaining: driverHoursStatus === "partial" ? Number(drivingHoursRemaining) : undefined,
          currentMovementStatus,
          ...routeRisk,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setResult(data as PredictionResult);
    } catch (e: any) {
      toast({ title: "Analysis failed", description: e.message || "Something went wrong.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };


  const riskColor = (level: string) => {
    const l = level?.toLowerCase();
    if (l === "low") return "text-success";
    if (l === "medium" || l === "moderate") return "text-warning";
    return "text-destructive";
  };

  const riskBg = (level: string) => {
    const l = level?.toLowerCase();
    if (l === "low") return "bg-success/10 text-success";
    if (l === "medium" || l === "moderate") return "bg-warning/10 text-warning";
    return "bg-destructive/10 text-destructive";
  };

  const toggleBtnCls = (active: boolean) =>
    cn(
      "px-3 py-1 rounded-md text-xs font-semibold capitalize transition-colors border",
      active
        ? "bg-primary text-primary-foreground border-primary"
        : "bg-muted text-muted-foreground border-border hover:bg-secondary"
    );

  return (
    <div className="w-full min-w-0 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <Beaker className="h-6 w-6 text-primary" />
          <DemoHint hintKey="page.freightLab" side="bottom" showIndicator>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">Freight Lab</h1>
          </DemoHint>
          <Badge variant="secondary" className="text-[10px] font-semibold gap-1 px-2 py-0.5 bg-primary/10 text-primary border-0">
            <Sparkles className="h-3 w-3" /> Powered by ATLAS AI Engine
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground max-w-xl">
          Experimental freight intelligence tools built with feedback from brokers, carriers, and dispatchers.
        </p>
        <Badge variant="outline" className="w-fit text-[10px] mt-1 text-muted-foreground border-border">
          Experimental
        </Badge>
      </div>

      {/* Main Layout: Hero Left + Panels Right */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* LEFT — LanePredictor Hero (70%) */}
        <div className="w-full lg:w-[70%]">
          <Card className="border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-bold text-foreground flex items-center gap-2">
                  LanePredictor
                  <Badge variant="outline" className="text-[10px] font-mono">V1.0</Badge>
                </CardTitle>
                <Badge className="bg-success text-success-foreground text-[10px] border-0">LIVE</Badge>
              </div>
              <CardDescription className="text-xs">
                Predict if a truck will deliver on time using tracking logs and route analysis.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Section 1 — Lane Setup */}
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Lane Setup</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Origin<span style={{ color: "hsl(var(--danger))" }}> *</span></Label>
                    <LocationAutocomplete
                      value={origin}
                      onChange={handleOriginChange}
                      placeholder="e.g. Dallas, TX"
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Destination<span style={{ color: "hsl(var(--danger))" }}> *</span></Label>
                    <LocationAutocomplete
                      value={destination}
                      onChange={handleDestinationChange}
                      placeholder="e.g. Columbus, OH"
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Truck Type</Label>
                    <div className="flex gap-1 mt-1">
                      {(["solo", "team"] as const).map(t => (
                        <button key={t} onClick={() => setTruckType(t)} className={toggleBtnCls(truckType === t)}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Total Trip Miles <span className="text-muted-foreground">(origin→dest)</span></Label>
                    <div className="relative">
                      <Input type="number" placeholder="2400" value={miles} onChange={e => setMiles(e.target.value)} className="h-8 text-xs" />
                      {milesLoading && (
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <Label className="text-xs">Remaining Miles</Label>
                    <p className="text-[10px] text-muted-foreground mb-1">Auto-calculates from tracking evidence or current truck location</p>
                    <div className="relative h-8 px-2 flex items-center rounded-md border border-dashed border-input bg-muted/30">
                      {remainingMiles ? (
                        <span className="inline-flex items-center rounded-md bg-brand-teal-subtle text-brand-teal-deep px-2 py-0.5 text-xs font-semibold">
                          {remainingMiles} mi
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground truncate">— Pending location</span>
                      )}
                      {remainingMilesLoading && (
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    {remainingMilesAuto && (
                      <p className="text-[10px] text-success mt-0.5">
                        ✓ Auto-calculated from {trackingLogs.trim() ? "tracking logs" : "current location"}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 2 — Appointments (date picker + time text) */}
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Appointments</p>
                <div className="grid grid-cols-2 gap-3">
                  {/* Pickup Date */}
                  <div>
                    <Label className="text-xs">Pickup Date<span style={{ color: "hsl(var(--danger))" }}> *</span></Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn("w-full h-8 justify-start text-left text-xs font-normal bg-background border-input", !pickupDate && "text-muted-foreground")}
                        >
                          <CalendarIcon className="mr-2 h-3 w-3" />
                          {pickupDate ? format(pickupDate, "PPP") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={pickupDate} onSelect={setPickupDate} initialFocus className="p-3 pointer-events-auto" />
                      </PopoverContent>
                    </Popover>
                  </div>
                  {/* Pickup Time */}
                  <div>
                    <Label className="text-xs">Pickup Time<span style={{ color: "hsl(var(--danger))" }}> *</span></Label>
                    <Input
                      placeholder="e.g. 10am, 14:00, 0800"
                      value={pickupTimeText}
                      onChange={e => { setPickupTimeText(e.target.value); setPickupTimeError(""); }}
                      className={cn("h-8 text-xs", pickupTimeError && "border-destructive focus-visible:ring-destructive")}
                    />
                    {pickupTimeError && <p className="text-[10px] text-destructive mt-0.5">{pickupTimeError}</p>}
                  </div>
                  {/* Delivery Date */}
                  <div>
                    <Label className="text-xs">Delivery Date<span style={{ color: "hsl(var(--danger))" }}> *</span></Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn("w-full h-8 justify-start text-left text-xs font-normal bg-background border-input", !deliveryDate && "text-muted-foreground")}
                        >
                          <CalendarIcon className="mr-2 h-3 w-3" />
                          {deliveryDate ? format(deliveryDate, "PPP") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={deliveryDate} onSelect={setDeliveryDate} initialFocus className="p-3 pointer-events-auto" />
                      </PopoverContent>
                    </Popover>
                  </div>
                  {/* Delivery Time */}
                  <div>
                    <Label className="text-xs">Delivery Time<span style={{ color: "hsl(var(--danger))" }}> *</span></Label>
                    <Input
                      placeholder="e.g. 2pm, 22:30, 1200 hrs"
                      value={deliveryTimeText}
                      onChange={e => { setDeliveryTimeText(e.target.value); setDeliveryTimeError(""); }}
                      className={cn("h-8 text-xs", deliveryTimeError && "border-destructive focus-visible:ring-destructive")}
                    />
                    {deliveryTimeError && <p className="text-[10px] text-destructive mt-0.5">{deliveryTimeError}</p>}
                  </div>
                  {/* Time Now (Delivery TZ) */}
                  <div className="col-span-2">
                    <Label className="text-xs">Time Now <span className="text-muted-foreground">(delivery timezone reference)</span></Label>
                    <Input
                      placeholder="e.g. 11:52am"
                      value={analysisTimeText}
                      onChange={e => { setAnalysisTimeText(e.target.value); setAnalysisTimeError(""); }}
                      className={cn("h-8 text-xs max-w-[200px]", analysisTimeError && "border-destructive focus-visible:ring-destructive")}
                    />
                    {analysisTimeError && <p className="text-[10px] text-destructive mt-0.5">{analysisTimeError}</p>}
                    <p className="text-[10px] text-muted-foreground mt-0.5">Auto-populated. Edit if analysis time differs from now.</p>
                  </div>
                </div>
              </div>

              {/* Section 2b — Driver Status */}
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Driver Status</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Driver Hours Status</Label>
                    <div className="grid grid-cols-3 gap-1 mt-1 w-full">
                      {([
                        { value: "fresh" as const, label: "Fresh Shift" },
                        { value: "partial" as const, label: "Partial Hours" },
                        { value: "unknown" as const, label: "Unknown" },
                      ]).map(opt => (
                        <button key={opt.value} onClick={() => setDriverHoursStatus(opt.value)} className={cn(toggleBtnCls(driverHoursStatus === opt.value), "w-full min-w-0 truncate")}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Current Movement Status</Label>
                    <div className="grid grid-cols-3 gap-1 mt-1 w-full">
                      {([
                        { value: "moving" as const, label: "Moving Now" },
                        { value: "parked" as const, label: "Parked Now" },
                        { value: "unknown" as const, label: "Unknown" },
                      ]).map(opt => (
                        <button key={opt.value} onClick={() => setCurrentMovementStatus(opt.value)} className={cn(toggleBtnCls(currentMovementStatus === opt.value), "w-full min-w-0 truncate")}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {driverHoursStatus === "partial" && (
                  <div className="mt-2 max-w-[200px]">
                    <Label className="text-xs">Driving Hours Remaining</Label>
                    <Input
                      type="number"
                      min={0}
                      max={11}
                      placeholder="e.g. 6"
                      value={drivingHoursRemaining}
                      onChange={e => setDrivingHoursRemaining(e.target.value)}
                      className="h-8 text-xs"
                    />
                  </div>
                )}
              </div>

              {/* Section 3 — Tracking Evidence */}
              <div>
                <div className="flex items-baseline justify-between mb-1">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Tracking Evidence</p>
                  <p className="text-[10px] text-muted-foreground">Required: enter one of the below</p>
                </div>
                {trackingError && (
                  <p className="text-[11px] text-destructive mb-2">{trackingError}</p>
                )}
                <Textarea
                  placeholder={TRACKING_PLACEHOLDER}
                  value={trackingLogs}
                  onChange={e => {
                    setTrackingLogs(e.target.value);
                    remainingMilesManualRef.current = false;
                    if (e.target.value.trim()) {
                      setCurrentLocationOverride("");
                      setCurrentLocationPlaceData(null);
                      setTrackingError("");
                    }
                  }}
                  className={cn("text-xs min-h-[80px] resize-none", trackingError && "border-destructive focus-visible:ring-destructive")}
                />

                {/* Current location fallback — shown when no tracking logs */}
                {!trackingLogs.trim() && (
                  <div className={cn("mt-3 p-3 rounded-md border border-dashed border-border bg-muted/30", trackingError && "border-destructive")}>
                    <p className="text-xs font-medium text-foreground mb-1">Enter tracking logs OR current truck location below</p>
                    <p className="text-[10px] text-muted-foreground mb-2">Remaining miles will auto-calculate from this location to destination</p>
                    <LocationAutocomplete
                      value={currentLocationOverride}
                      onChange={(val, placeData) => {
                        setCurrentLocationOverride(val);
                        if (placeData) {
                          setCurrentLocationPlaceData(placeData);
                          remainingMilesManualRef.current = false;
                          setTrackingError("");
                        }
                      }}
                      placeholder="e.g. Indianapolis, IN"
                      className="h-8 text-xs"
                    />
                  </div>
                )}
              </div>

              <Button onClick={handleAnalyze} disabled={loading || remainingMilesLoading} className="w-full h-10 text-sm font-bold">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Analyzing…</> : remainingMilesLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Calculating remaining miles…</> : "Analyze Delivery"}
              </Button>

              {/* ─── Results ─── */}
              {result && (
                <div className="space-y-4 border-t border-border pt-4">
                  {/* Top Result Strip */}
                  <div className="flex flex-wrap items-center gap-3 bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      {(result.prediction === "on_time" || result.prediction?.toLowerCase().includes("on time"))
                        ? <CheckCircle className="h-5 w-5 text-success" />
                        : (result.prediction === "tight" || result.prediction?.toLowerCase().includes("tight"))
                          ? <Clock className="h-5 w-5 text-warning" />
                          : <AlertTriangle className="h-5 w-5 text-destructive" />}
                      <span className="text-base font-bold text-foreground">{fmtPrediction(result.prediction)}</span>
                    </div>
                    <Badge variant="outline" className="text-xs font-semibold">{result.confidence}% Confidence</Badge>
                    <Badge className={cn("text-[10px] font-semibold border-0", riskBg(result.riskLevel))}>
                      <ShieldAlert className="h-3 w-3 mr-1" /> {fmtRisk(result.riskLevel)} Risk
                    </Badge>
                  </div>

                  {/* Route Conditions Panel */}
                  {(result.weatherImpact || result.trafficImpact || result.constructionImpact) && (
                    <div>
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Route Conditions</p>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { key: "weather", data: result.weatherImpact, icon: CloudRain, label: "Weather" },
                          { key: "traffic", data: result.trafficImpact, icon: Car, label: "Traffic" },
                          { key: "construction", data: result.constructionImpact, icon: Construction, label: "Construction" },
                        ].map(({ key, data, icon: Icon, label }) => {
                          if (!data) return null;
                          const sev = data.severity || "none";
                          const sevColors: Record<string, string> = {
                            none: "bg-muted text-muted-foreground",
                            low: "bg-success/10 text-success",
                            moderate: "bg-warning/10 text-warning",
                            high: "bg-destructive/10 text-destructive",
                          };
                          const badgeColors: Record<string, string> = {
                            none: "bg-muted text-muted-foreground",
                            low: "bg-success/15 text-success border-success/20",
                            moderate: "bg-warning/15 text-warning border-warning/20",
                            high: "bg-destructive/15 text-destructive border-destructive/20",
                          };
                          return (
                            <div key={key} className="bg-card border border-border rounded-lg p-2.5 space-y-1.5">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5">
                                  <div className={cn("h-6 w-6 rounded flex items-center justify-center", sevColors[sev])}>
                                    <Icon className="h-3 w-3" />
                                  </div>
                                  <span className="text-[10px] font-semibold text-foreground">{label}</span>
                                </div>
                                <Badge className={cn("text-[9px] font-semibold border px-1.5 py-0", badgeColors[sev])}>
                                  {sev === "none" ? "Clear" : sev.charAt(0).toUpperCase() + sev.slice(1)}
                                </Badge>
                              </div>
                              <p className="text-[10px] text-muted-foreground leading-snug">{data.summary}</p>
                              {data.delayMinutes > 0 && (
                                <p className="text-[10px] font-medium text-foreground">+{data.delayMinutes} min delay</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* ETA Summary */}
                  {(result.estimatedArrival || result.bufferHours !== undefined) && (
                    <div className="bg-card border border-border rounded-lg p-3">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">ETA Summary</p>
                      <div className="flex items-center gap-4">
                        {result.estimatedArrival && (
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center">
                              <Timer className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <p className="text-[10px] text-muted-foreground">Est. Arrival</p>
                              <p className="text-sm font-bold text-foreground">{result.estimatedArrival}</p>
                            </div>
                          </div>
                        )}
                        {result.bufferHours !== undefined && (
                          <div className="flex items-center gap-2">
                            <div className={cn("h-8 w-8 rounded-md flex items-center justify-center",
                              result.bufferHours < 0 ? "bg-destructive/10" : result.bufferHours < 2 ? "bg-warning/10" : "bg-success/10"
                            )}>
                              <Clock className={cn("h-4 w-4",
                                result.bufferHours < 0 ? "text-destructive" : result.bufferHours < 2 ? "text-warning" : "text-success"
                              )} />
                            </div>
                            <div>
                              <p className="text-[10px] text-muted-foreground">Buffer</p>
                              <p className={cn("text-sm font-bold",
                                result.bufferHours < 0 ? "text-destructive" : result.bufferHours < 2 ? "text-warning" : "text-success"
                              )}>
                                {result.bufferHours < 0 ? `${Math.abs(result.bufferHours).toFixed(1)}h late` : `${result.bufferHours.toFixed(1)}h`}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Secondary Insight Blocks */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-card border border-border rounded-lg p-3 flex items-center gap-3">
                      <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center">
                        <RouteIcon className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Remaining Miles</p>
                        <p className="text-sm font-bold text-foreground">{result.remainingMiles} mi</p>
                      </div>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-3 flex items-center gap-3">
                      <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center">
                        <Gauge className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Required Speed</p>
                        <p className="text-sm font-bold text-foreground">{result.requiredSpeed} MPH</p>
                      </div>
                    </div>
                  </div>

                  {/* Detected Issues */}
                  {result.detectedIssues?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Detected Issues</p>
                      <ul className="space-y-1.5">
                        {result.detectedIssues.map((issue, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-foreground">
                            <AlertTriangle className="h-3.5 w-3.5 text-warning mt-0.5 shrink-0" />
                            {issue}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Recommendation */}
                  {result.recommendation && (
                    <div className="bg-muted rounded-lg p-3">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Recommendation</p>
                      <p className="text-xs text-foreground leading-relaxed">{result.recommendation}</p>
                    </div>
                  )}

                  {/* Why ATLAS thinks this */}
                  {result.reasoning?.length > 0 && (
                    <Collapsible open={reasoningOpen} onOpenChange={setReasoningOpen}>
                      <CollapsibleTrigger className="flex items-center gap-2 w-full text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors py-1">
                        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", reasoningOpen && "rotate-180")} />
                        Why ATLAS thinks this
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pt-2 space-y-1.5">
                        {result.reasoning.map((point, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                            <TrendingUp className="h-3 w-3 mt-0.5 shrink-0 text-primary" />
                            {point}
                          </div>
                        ))}
                      </CollapsibleContent>
                    </Collapsible>
                  )}

                  {/* Feedback Row */}
                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border">
                    <span className="text-[11px] text-muted-foreground mr-1">Was this useful?</span>
                    <button
                      onClick={() => castVote(CURRENT_FEATURE_KEY, "helpful")}
                      className={cn("flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border transition-colors",
                        currentFeature?.user_voted_helpful ? "bg-success/10 border-success text-success" : "border-border text-muted-foreground hover:bg-muted"
                      )}
                    >
                      <ThumbsUp className="h-3 w-3" /> Helpful <span className="font-semibold">{Number(currentFeature?.helpful_count ?? 0)}</span>
                    </button>
                    <button
                      onClick={() => castVote(CURRENT_FEATURE_KEY, "not_helpful")}
                      className={cn("flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border transition-colors",
                        currentFeature?.user_voted_not_helpful ? "bg-destructive/10 border-destructive text-destructive" : "border-border text-muted-foreground hover:bg-muted"
                      )}
                    >
                      <ThumbsDown className="h-3 w-3" /> Not Helpful <span className="font-semibold">{Number(currentFeature?.not_helpful_count ?? 0)}</span>
                    </button>
                    <button
                      onClick={() => castVote(CURRENT_FEATURE_KEY, "make_core")}
                      className={cn("flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border transition-colors",
                        currentFeature?.user_voted_make_core ? "bg-warning/10 border-warning text-warning" : "border-border text-muted-foreground hover:bg-muted"
                      )}
                    >
                      <Star className="h-3 w-3" /> Make Core Feature <span className="font-semibold">{Number(currentFeature?.make_core_count ?? 0)}</span>
                    </button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT — Stacked Panels (30%) */}
        <div className="w-full lg:w-[30%] flex flex-col gap-4">
          {/* Next in the Lab */}
          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold text-foreground">Next in the Lab</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold text-foreground">Load Check</span>
                  <Badge variant="outline" className="text-[9px] text-muted-foreground border-border">Coming Soon</Badge>
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Check whether a load offer is realistically serviceable before booking.
                </p>
              </div>
              <div className="bg-muted rounded-md p-2.5">
                <p className="text-[10px] text-muted-foreground leading-relaxed">
                  ATLAS will compare miles, appointments, truck type, traffic patterns, and route pressure to verify if the load is serviceable.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs h-8"
                  onClick={() => castVote("load_check", "prioritize")}
                >
                  <Vote className="h-3 w-3 mr-1.5" />
                  {loadCheckFeature?.user_voted_prioritize ? "Voted ✓" : "Vote to Prioritize"}{" "}
                  <span className="font-semibold ml-1">{Number(loadCheckFeature?.prioritize_count ?? 0)}</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs h-8"
                  onClick={() => setNotify("load_check")}
                  disabled={!!loadCheckFeature?.user_notified}
                >
                  <Bell className="h-3 w-3 mr-1.5" />
                  {loadCheckFeature?.user_notified ? "You'll be notified ✓" : "Notify Me When Live"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Community Feedback */}
          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold text-foreground">Community Feedback</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Lab Status</span>
                <Badge className="bg-success/10 text-success border-0 text-[10px]">
                  {labStatusLabel[currentFeature?.status ?? "live"] || "Live"}
                </Badge>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Community Votes</span>
                <span className="font-bold text-foreground">{communityVotesTotal}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Most Requested</span>
                <span className="font-bold text-foreground">{mostRequested?.display_name ?? "—"}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Active Testers</span>
                <span className="font-bold text-foreground">{Number(currentFeature?.active_testers_7d ?? 0)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Bottom Teaser Strip */}
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="rounded-lg border border-border bg-card p-6 flex flex-col items-center justify-center blur-[3px] opacity-30 select-none">
            <div className="h-3 w-24 rounded bg-muted mb-2" />
            <div className="h-2.5 w-32 rounded bg-muted" />
          </div>
        ))}
      </div>
      <div className="flex justify-center -mt-3">
        <Badge variant="outline" className="text-[9px] text-muted-foreground border-border">
          Coming Soon
        </Badge>
      </div>
    </div>
  );
}
